import {
  IconBolt,
  IconFolder,
  IconFolderOpen,
  IconLock,
  IconMail,
} from '@tabler/icons-react';
import React from 'react';
import { accessKindForFolder } from 'src/app/documents/documentAccess';
import {
  DOCUMENT_FOLDER_TYPE,
  ROOT_FOLDER_ID,
  isRestrictedAccess,
} from 'src/app/documents/documentConstants';
import type { IFolderNode } from 'src/app/documents/folderNode';
import { useExpandedFolderState } from 'src/app/documents/useExpandedFolderState';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { accessIndicatorLabel } from 'src/design/molecules/AccessIndicator';
import { Tree, useTree } from 'src/design/molecules/Tree';
import type {
  RenderTreeNodePayload,
  TreeNodeData,
} from 'src/design/molecules/Tree';
import { TreeRow } from 'src/design/molecules/TreeRow';
import { useAuth } from 'src/hooks/useAuth';
import classes from './FolderTree.module.css';

export interface IFolderTreeProps {
  folders: IFolderNode[];
  selectedFolderId: string;
  onFolderSelected: (folderId: string) => void;
  rootLabel?: string;
}

const toNode = (folder: IFolderNode): TreeNodeData => ({
  value: folder.id,
  label: folder.name,
  nodeProps: { folder },
  children: (folder.children ?? []).map(toNode),
});

/** @registry Folder sidebar shared by the job Documents module and the settings folder templates: a Tree over the nested folder list with a synthetic root node, restricted/email/system indicators and a document count; emits the selected folder id (ROOT_FOLDER_ID for the root). */
export const FolderTree: React.FC<IFolderTreeProps> = ({
  folders,
  selectedFolderId,
  onFolderSelected,
  rootLabel = 'All documents',
}) => {
  const { user } = useAuth();
  const currentUserId = user?.id ?? -1;
  const tree = useTree();
  const { setExpandedState, setSelectedState } = tree;

  useExpandedFolderState(folders, setExpandedState);

  React.useEffect(() => {
    setSelectedState((current) =>
      current.length === 1 && current[0] === selectedFolderId
        ? current
        : [selectedFolderId],
    );
  }, [selectedFolderId, setSelectedState]);

  const data = React.useMemo<TreeNodeData[]>(
    () => [
      {
        value: ROOT_FOLDER_ID,
        label: rootLabel,
        children: folders.map(toNode),
      },
    ],
    [folders, rootLabel],
  );

  const renderNode = (payload: RenderTreeNodePayload) => {
    const { node, expanded, selected } = payload;
    const folder = node.nodeProps?.folder as IFolderNode | undefined;
    const isSystem = folder?.isLocked ?? false;
    const documentCount = folder?.documents?.length ?? 0;
    const FolderIcon = expanded || selected ? IconFolderOpen : IconFolder;

    return (
      <TreeRow
        payload={payload}
        onSelect={onFolderSelected}
        label={node.label}
        icon={
          <FolderIcon
            size={14}
            className={isSystem ? classes.systemFolderIcon : classes.folderIcon}
          />
        }
        trailing={
          <>
            {folder && isRestrictedAccess(folder.access) && (
              <Tooltip
                label={accessIndicatorLabel(
                  accessKindForFolder(folder, currentUserId),
                )}
              >
                <IconLock size={12} className={classes.indicator} />
              </Tooltip>
            )}
            {folder?.folderType === DOCUMENT_FOLDER_TYPE.emails && (
              <Tooltip label="Email inbox">
                <IconMail size={12} className={classes.indicator} />
              </Tooltip>
            )}
            {isSystem && (
              <Tooltip label="System folder">
                <IconBolt size={12} className={classes.indicator} />
              </Tooltip>
            )}
            {documentCount > 0 && (
              <Text size="xs" c="dimmed">
                {documentCount}
              </Text>
            )}
          </>
        }
      />
    );
  };

  return (
    <Tree
      aria-label="Folders"
      data={data}
      tree={tree}
      renderNode={renderNode}
    />
  );
};
